import { Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";

import { ChevronRight, Search, Loader2, Plus, Building2, Users } from "lucide-react";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { type NavGroup, type NavMainItem } from "@/navigation/sidebar/sidebar-items";
import { useSchools } from "@/hooks/use-schools";
import { useSearchUsers } from "@/hooks/use-users";
import { SchoolLogo } from "@/components/school-logo";
import { Badge } from "@/components/ui/badge";
import { UserDetailDialog } from "@/components/users/user-detail-dialog";
import type { User, UserSearchResult } from "@/types/user";

interface NavMainProps {
  readonly items: readonly NavGroup[];
}

const IsComingSoon = () => (
  <span className="ml-auto rounded-md bg-gray-200 px-2 py-1 text-xs dark:text-gray-800">Soon</span>
);

const NavItemExpanded = ({
  item,
  isActive,
  isSubmenuOpen,
  onActionClick,
  showActionButton,
}: {
  item: NavMainItem;
  isActive: (url: string, subItems?: NavMainItem["subItems"]) => boolean;
  isSubmenuOpen: (subItems?: NavMainItem["subItems"]) => boolean;
  onActionClick?: () => void;
  showActionButton?: boolean;
}) => {
  return (
    <Collapsible key={item.title} asChild defaultOpen={isSubmenuOpen(item.subItems)} className="group/collapsible">
      <SidebarMenuItem>
        <div className="relative flex items-center gap-1">
          <CollapsibleTrigger asChild className="flex-1">
            {item.subItems ? (
              <SidebarMenuButton
                disabled={item.comingSoon}
                isActive={isActive(item.url, item.subItems)}
                tooltip={item.title}
              >
                {item.icon && <item.icon />}
                <span>{item.title}</span>
                {item.comingSoon && <IsComingSoon />}
                <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              </SidebarMenuButton>
            ) : (
              <SidebarMenuButton
                asChild
                aria-disabled={item.comingSoon}
                isActive={isActive(item.url)}
                tooltip={item.title}
              >
                <Link to={item.url} target={item.newTab ? "_blank" : undefined}>
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                  {item.comingSoon && <IsComingSoon />}
                </Link>
              </SidebarMenuButton>
            )}
          </CollapsibleTrigger>
          {showActionButton && onActionClick && (
            <button
              onClick={onActionClick}
              className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              title="Create School"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>
        {item.subItems && (
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.subItems.map((subItem) => (
                <SidebarMenuSubItem key={subItem.title}>
                  <SidebarMenuSubButton aria-disabled={subItem.comingSoon} isActive={isActive(subItem.url)} asChild>
                    <Link to={subItem.url} target={subItem.newTab ? "_blank" : undefined}>
                      {subItem.icon && <subItem.icon />}
                      <span>{subItem.title}</span>
                      {subItem.comingSoon && <IsComingSoon />}
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        )}
      </SidebarMenuItem>
    </Collapsible>
  );
};

const NavItemCollapsed = ({
  item,
  isActive,
}: {
  item: NavMainItem;
  isActive: (url: string, subItems?: NavMainItem["subItems"]) => boolean;
}) => {
  return (
    <SidebarMenuItem key={item.title}>
      {item.subItems ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              disabled={item.comingSoon}
              tooltip={item.title}
              isActive={isActive(item.url, item.subItems)}
            >
              {item.icon && <item.icon />}
              <span>{item.title}</span>
              <ChevronRight />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-50 space-y-1" side="right" align="start">
            {item.subItems?.map((subItem) => (
              <DropdownMenuItem key={subItem.title} asChild>
                <SidebarMenuSubButton
                  key={subItem.title}
                  asChild
                  className="focus-visible:ring-0"
                  aria-disabled={subItem.comingSoon}
                  isActive={isActive(subItem.url)}
                >
                  <Link to={subItem.url} target={subItem.newTab ? "_blank" : undefined}>
                    {subItem.icon && <subItem.icon className="[&>svg]:text-sidebar-foreground" />}
                    <span>{subItem.title}</span>
                    {subItem.comingSoon && <IsComingSoon />}
                  </Link>
                </SidebarMenuSubButton>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <SidebarMenuButton
          asChild
          disabled={item.comingSoon}
          tooltip={item.title}
          isActive={isActive(item.url, item.subItems)}
        >
          <Link to={item.url}>
            {item.icon && <item.icon />}
            <span>{item.title}</span>
          </Link>
        </SidebarMenuButton>
      )}
    </SidebarMenuItem>
  );
};

type SearchMode = "schools" | "users";

export function NavMain({ items }: NavMainProps) {
  const location = useLocation();
  const path = location.pathname;
  const navigate = useNavigate();
  const { state, isMobile, setOpen } = useSidebar();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [shouldFocusSearch, setShouldFocusSearch] = useState(false);
  const [searchMode, setSearchMode] = useState<SearchMode>("schools");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch schools when there's a search query and mode is schools
  const { data: schoolsData, isLoading: isLoadingSchools } = useSchools({
    search: searchMode === "schools" && debouncedQuery ? debouncedQuery : undefined,
    limit: 10,
    paginate: true,
  });

  // Fetch users when there's a search query and mode is users
  const { data: usersData = [], isLoading: isLoadingUsers } = useSearchUsers(
    searchMode === "users" && debouncedQuery.length >= 4 ? debouncedQuery : ""
  );

  const isLoading = searchMode === "schools" ? isLoadingSchools : isLoadingUsers;

  // Show/hide results based on query and focus
  useEffect(() => {
    setShowResults(searchQuery.length > 0);
  }, [searchQuery]);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when sidebar expands (only when triggered by search icon click)
  useEffect(() => {
    if (state === "expanded" && shouldFocusSearch && searchInputRef.current) {
      // Small delay to ensure the input is rendered
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
        setShouldFocusSearch(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [state, shouldFocusSearch]);

  const handleSchoolSelect = (schoolName: string) => {
    setSearchQuery("");
    setShowResults(false);
    navigate(`/dashboard/schools/${schoolName}`);
  };

  const handleUserSelect = (user: UserSearchResult) => {
    setSearchQuery("");
    setShowResults(false);
    // Convert UserSearchResult to User type for the dialog
    const fullUser: User = {
      ...user,
      isAmbassador: false, // We don't have this in search results
    };
    setSelectedUser(fullUser);
    setUserDialogOpen(true);
  };

  const handleSearchIconClick = () => {
    setShouldFocusSearch(true);
    setOpen(true);
  };

  const toggleSearchMode = () => {
    const newMode = searchMode === "schools" ? "users" : "schools";
    setSearchMode(newMode);
    setSearchQuery("");
    setShowResults(false);
  };

  const handleCreateSchool = () => {
    navigate("/dashboard/schools/new");
  };

  const isItemActive = (url: string, subItems?: NavMainItem["subItems"]) => {
    if (subItems?.length) {
      return subItems.some((sub) => path.startsWith(sub.url));
    }
    return path === url;
  };

  const isSubmenuOpen = (subItems?: NavMainItem["subItems"]) => {
    return subItems?.some((sub) => path.startsWith(sub.url)) ?? false;
  };

  return (
    <>
      <SidebarGroup>
        <SidebarGroupContent className="flex flex-col gap-2">
          {state === "collapsed" && !isMobile ? (
            // Collapsed state: Show just the search icon
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={handleSearchIconClick}
                  tooltip="Search"
                  className="justify-center"
                >
                  <Search />
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          ) : (
            // Expanded state: Show full search input
            <div className="relative px-2" ref={searchRef}>
              {/* Search Input with integrated mode toggle */}
              <div className="relative">
                <button
                  onClick={toggleSearchMode}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-6 w-6 flex items-center justify-center rounded hover:bg-accent transition-colors"
                  title={`Switch to ${searchMode === "schools" ? "users" : "schools"}`}
                >
                  {searchMode === "schools" ? (
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Users className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
                <Input
                  ref={searchInputRef}
                  type="search"
                  placeholder={searchMode === "schools" ? "Search schools..." : "Search users..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.length > 0 && setShowResults(true)}
                  className="pl-9 pr-3 h-9"
                />
              </div>
              
              {/* Search Results Dropdown */}
              {showResults && (
                <div className="absolute top-full left-2 right-2 mt-1 bg-popover border rounded-md shadow-lg z-50 max-h-[300px] overflow-y-auto">
                  {isLoading ? (
                    <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Searching...
                    </div>
                  ) : searchMode === "schools" ? (
                    // Schools results
                    schoolsData?.schools && schoolsData.schools.length > 0 ? (
                      <div className="py-1">
                        {schoolsData.schools.map((school) => (
                          <button
                            key={school.schoolName}
                            onClick={() => handleSchoolSelect(school.schoolName)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors flex items-center gap-2"
                          >
                            <SchoolLogo school={school} size="sm" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium">{school.schoolTitle}</div>
                              {(school.state || school.country) && (
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {[school.state, school.country].filter(Boolean).join(", ")}
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 text-sm text-muted-foreground text-center">
                        No schools found
                      </div>
                    )
                  ) : (
                    // Users results
                    searchQuery.length < 4 ? (
                      <div className="p-4 text-sm text-muted-foreground text-center">
                        Enter at least 4 characters
                      </div>
                    ) : usersData.length > 0 ? (
                      <div className="py-1">
                        {usersData.slice(0, 10).map((user) => (
                          <button
                            key={user.id}
                            onClick={() => handleUserSelect(user)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                          >
                            <div className="font-medium">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                              {user.email && <span>{user.email}</span>}
                              {user.schoolId && (
                                <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                  {user.schoolId}
                                </Badge>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 text-sm text-muted-foreground text-center">
                        No users found
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          )}
        </SidebarGroupContent>
      </SidebarGroup>
      {items.map((group) => (
        <SidebarGroup key={group.id}>
          {group.label && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
          <SidebarGroupContent className="flex flex-col gap-3">
            <SidebarMenu className="gap-2">
              {group.items.map((item) => {
                if (state === "collapsed" && !isMobile) {
                  // Collapsed view - no action buttons
                  return (
                    <NavItemCollapsed 
                      key={item.title} 
                      item={item} 
                      isActive={isItemActive}
                    />
                  );
                }
                // Expanded view - show action buttons
                return (
                  <NavItemExpanded 
                    key={item.title} 
                    item={item} 
                    isActive={isItemActive} 
                    isSubmenuOpen={isSubmenuOpen}
                    onActionClick={item.title === "Schools" ? handleCreateSchool : undefined}
                    showActionButton={item.title === "Schools"}
                  />
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}

      {/* User Detail Dialog */}
      <UserDetailDialog
        user={selectedUser}
        open={userDialogOpen}
        onOpenChange={setUserDialogOpen}
      />
    </>
  );
}
